// Cross-platform voice-playback driver interface. The shared
// `<VoiceMessageBubble>` and its host hook (`useVoicePlayback`) stay
// completely audio-API-agnostic — each host (apps/web,
// apps/desktop) provides a driver that knows how to play a
// platform-native audio source and decode a waveform from it.
//
// Web   — wraps `new Audio(blobUrl)` + `AudioContext.decodeAudioData`.
// Desktop — wraps `react-native-audio-recorder-player` + a stable
//           deterministic waveform (the native lib doesn't expose
//           per-sample amplitude data today; that lands in a
//           follow-up).
//
// The driver is injected through `<VoicePlaybackProvider>` so the
// shared layer (`useVoicePlayback`, `VoiceMessageBubble`) compiles
// without pulling in any host-side audio plumbing.

export type VoiceSource =
  | { readonly kind: 'web-blob'; readonly blobUrl: string }
  | { readonly kind: 'macos-base64'; readonly base64: string; readonly mime: string };

export interface VoicePlaybackHandle {
  /** Stops playback and releases any platform-level audio resources. */
  readonly stop: () => void;
  /** Adjust playback speed live (1 = normal). Optional for older drivers. */
  readonly setRate?: ((rate: number) => void) | undefined;
}

export interface VoicePlaybackDriver {
  /**
   * Starts playback of `source`. Resolves to a `VoicePlaybackHandle`
   * once the underlying audio element is wired up. `onProgress` is
   * invoked with elapsed ms (≈ 30 Hz cadence) and `onEnded` once
   * playback completes naturally.
   */
  play(opts: {
    readonly source: VoiceSource;
    readonly onProgress: (progressMs: number) => void;
    readonly onEnded: () => void;
    /**
     * Fired once the element knows the clip's real duration (ms). Lets
     * the bubble compute the played fraction even when the envelope's
     * recorded `durationMs` is missing/0 (common for MediaRecorder webm,
     * which omits duration metadata until finalised).
     */
    readonly onDuration?: ((durationMs: number) => void) | undefined;
    /** Initial playback speed (1 = normal). */
    readonly rate?: number | undefined;
    /** Surfaced when the element fails to decode/play (resets to idle). */
    readonly onError?: ((err: unknown) => void) | undefined;
  }): Promise<VoicePlaybackHandle>;

  /**
   * Extracts a normalised waveform (32 buckets, each 0..1) from
   * `source` for the bubble's amplitude visualisation. Implementations
   * that cannot decode audio (no AudioContext / no native peak data)
   * should return a deterministic fallback keyed off `seed` so the
   * bubble still renders a non-empty waveform.
   */
  extractWaveform(source: VoiceSource, seed?: string): Promise<number[]>;
}

/** Default bucket count for the bubble waveform. */
export const VOICE_WAVEFORM_BUCKETS = 32;

/**
 * Stable per-message waveform fallback. Deterministic on `seed` so the
 * same message renders the same bars across re-mounts — avoids a
 * "random reshuffle" feel until a native decoder is in place. The
 * exported helper is used by both the desktop driver (no AudioContext)
 * and by `useVoicePlayback`'s synchronous initial render before the
 * async `extractWaveform` resolves.
 */
export function deterministicWaveform(
  seed: string,
  bucketCount: number = VOICE_WAVEFORM_BUCKETS,
): number[] {
  // Cheap FNV-1a hash of the seed → starts the LCG with a value that
  // varies across message ids without pulling in a crypto dep.
  let state = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    state ^= seed.charCodeAt(i);
    state = (state + ((state << 1) + (state << 4) + (state << 7) + (state << 8) + (state << 24))) >>> 0;
  }
  const out: number[] = [];
  for (let i = 0; i < bucketCount; i++) {
    // 32-bit LCG (Numerical Recipes constants). Stable across hosts.
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    const norm = state / 0xffffffff;
    // Bias the result toward the 0.25..0.95 range so the bars read as
    // a voice envelope rather than a flat noise floor.
    out.push(0.25 + norm * 0.7);
  }
  return out;
}
